/**
 * Dữ liệu mẫu và máy chủ giả của màn Cơ cấu tổ chức (G2-D1-17, ADR-0014,
 * ADR-0067).
 *
 * Đóng vai user-service khi chưa đăng nhập Keycloak thật hoặc khi URL có
 * `?mock=`. Máy chủ giả **từ chối y như hợp đồng** — cùng mã HTTP, cùng
 * `pointer` — để nhánh lỗi chạy được khi chưa có backend:
 *
 * - Đổi cha thành chính nó hoặc con cháu: `422 DEPARTMENT_CYCLE` trỏ `/parent_id`.
 * - Cây vượt 10 cấp: `422 DEPARTMENT_TOO_DEEP` trỏ `/parent_id`.
 * - Xoá phòng ban còn người hoặc còn phòng con: `409 DEPARTMENT_NOT_EMPTY`.
 * - Lưu **Phòng Pháp chế** (`pb-phap-che`) lần đầu: `412 VERSION_MISMATCH` vì
 *   "một quản trị khác" vừa đổi tên nó ngay sau lúc hộp sửa đọc. Tải lại rồi lưu
 *   lần nữa thì qua.
 *
 * Số người của từng phòng ban đếm từ `mock/nguoi-dung.ts`, nên gán người vào
 * phòng ban ở màn người dùng làm số ở đây đổi theo.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây.
 */
import type { ApiError } from '../lib/errors';
import { DO_SAU_TOI_DA, duongToiGoc, nhanhCua, chieuCao } from '../modules/co-cau-to-chuc/nghiep-vu/cay';
import type {
  Department,
  DepartmentCoPhienBan,
  DepartmentCreate,
  DepartmentPage,
  DepartmentUpdate,
} from '../modules/co-cau-to-chuc/types';
import { soNguoiTheoPhongBan } from './nguoi-dung';

type PhongBanGoc = Omit<Department, 'user_count' | 'child_count'>;

const pb = (so: number, id: string, name: string, parent_id: string | null = null): PhongBanGoc => ({
  id,
  name,
  parent_id,
  created_at: `2026-09-${String(1 + so).padStart(2, '0')}T02:00:00Z`,
});

const DU_LIEU: PhongBanGoc[] = [
  pb(1, 'pb-bgd', 'Ban Giám đốc'),
  pb(2, 'pb-tc', 'Khối Tài chính'),
  pb(3, 'pb-ke-toan', 'Phòng Kế toán', 'pb-tc'),
  pb(4, 'pb-thue', 'Tổ Thuế', 'pb-ke-toan'),
  pb(5, 'pb-ngan-quy', 'Phòng Ngân quỹ', 'pb-tc'),
  pb(6, 'pb-kd', 'Khối Kinh doanh'),
  pb(7, 'pb-kd-mb', 'Phòng Kinh doanh miền Bắc', 'pb-kd'),
  pb(8, 'pb-kd-mn', 'Phòng Kinh doanh miền Nam', 'pb-kd'),
  pb(
    9,
    'pb-cskh',
    'Phòng Chăm sóc khách hàng doanh nghiệp và Đối tác chiến lược khu vực Đông Nam Bộ',
    'pb-kd',
  ),
  // Trống: xoá được ngay.
  pb(10, 'pb-ns', 'Phòng Nhân sự'),
  // Diễn `412`: xem đầu tệp.
  pb(11, 'pb-phap-che', 'Phòng Pháp chế'),
];

/** Phòng ban diễn `412` ở lần lưu đầu. */
const PHONG_XUNG_DOT = 'pb-phap-che';

let cacPhong: PhongBanGoc[] = structuredClone(DU_LIEU);
const phienBan = new Map<string, number>();
let daDienXungDot = false;
let soTao = 100;

const loi = (code: string, status: number, message: string, details?: unknown): ApiError => ({
  code,
  status,
  message,
  details,
});

const etag = (id: string) => `"v${phienBan.get(id) ?? 1}"`;
const tangPhienBan = (id: string) => phienBan.set(id, (phienBan.get(id) ?? 1) + 1);

const kemSo = (p: PhongBanGoc): Department =>
  structuredClone({
    ...p,
    user_count: soNguoiTheoPhongBan(p.id),
    child_count: cacPhong.filter((x) => x.parent_id === p.id).length,
  });

/** Bản đầy đủ của cả cây, cho các phép tính đường đi của `nghiep-vu/cay`. */
const tatCa = (): Department[] => cacPhong.map(kemSo);

function tim(id: string): PhongBanGoc {
  const p = cacPhong.find((x) => x.id === id);
  if (!p) {
    throw loi('DEPARTMENT_NOT_FOUND', 404, 'Không tìm thấy phòng ban này trong tenant. Tải lại danh sách phòng ban rồi chọn lại.');
  }
  return p;
}

function kiemTen(name: string): void {
  if (!name || name !== name.trim() || name.length > 200) {
    throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
      { pointer: '#/name', detail: 'Tên phòng ban từ 1 đến 200 ký tự, không có khoảng trắng ở đầu và cuối.' },
    ]);
  }
}

/** Cha có trong tenant và nhánh `caoNhanh` đặt dưới nó không vượt độ sâu. */
function kiemCha(parentId: string, caoNhanh: number): void {
  if (!cacPhong.some((x) => x.id === parentId)) {
    throw loi('DEPARTMENT_NOT_FOUND', 404, 'Không tìm thấy phòng ban cha đã chọn trong tenant. Tải lại cây phòng ban rồi chọn lại.', [
      { pointer: '#/parent_id', detail: 'Phòng ban này không còn trong tenant.' },
    ]);
  }
  if (duongToiGoc(tatCa(), parentId).length + 1 + caoNhanh > DO_SAU_TOI_DA) {
    throw loi('DEPARTMENT_TOO_DEEP', 422, `Cây phòng ban có tối đa ${DO_SAU_TOI_DA} cấp, phòng ban gốc là cấp 1. Chọn phòng ban cha ở cấp cao hơn.`, [
      { pointer: '#/parent_id', detail: 'Chọn phòng ban cha ở cấp cao hơn.' },
    ]);
  }
}

/** `mock/nguoi-dung.ts` hỏi khi gán người vào phòng ban. */
export const coPhongBan = (id: string): boolean => cacPhong.some((x) => x.id === id);

export const mayChuPhongBan = {
  /** `GET /api/v1/departments` — sắp `(created_at, id)` như backend; cursor là vị trí bắt đầu của trang sau. */
  danhSach({ limit, cursor }: { limit: number; cursor?: string }): DepartmentPage {
    const sap = [...cacPhong].sort(
      (a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id),
    );
    const tu = cursor ? Number(cursor) : 0;
    return {
      items: sap.slice(tu, tu + limit).map(kemSo),
      next_cursor: tu + limit < sap.length ? String(tu + limit) : null,
    };
  },

  /** `GET /api/v1/departments/{id}` kèm `ETag`. */
  chiTiet(id: string): DepartmentCoPhienBan {
    const kq = { ...kemSo(tim(id)), etag: etag(id) };
    if (id === PHONG_XUNG_DOT && !daDienXungDot) {
      // "Quản trị khác" đổi tên ngay sau lúc hộp sửa đọc.
      daDienXungDot = true;
      cacPhong = cacPhong.map((x) => (x.id === id ? { ...x, name: 'Phòng Pháp chế và Tuân thủ' } : x));
      tangPhienBan(id);
    }
    return kq;
  },

  /** `POST /api/v1/departments`. */
  tao(du: DepartmentCreate): DepartmentCoPhienBan {
    kiemTen(du.name);
    if (du.parent_id) kiemCha(du.parent_id, 0);
    soTao += 1;
    const moi: PhongBanGoc = {
      id: `pb-${soTao}`,
      name: du.name,
      parent_id: du.parent_id ?? null,
      created_at: new Date().toISOString(),
    };
    cacPhong = [...cacPhong, moi];
    return { ...kemSo(moi), etag: etag(moi.id) };
  },

  /** `PUT /api/v1/departments/{id}` với `If-Match`. */
  sua(id: string, du: DepartmentUpdate, ifMatch: string): DepartmentCoPhienBan {
    if (!ifMatch) {
      throw loi('PRECONDITION_REQUIRED', 428, 'Thiếu header If-Match. Đọc phòng ban để lấy ETag rồi gửi lại kèm If-Match.');
    }
    const p = tim(id);
    if (ifMatch !== etag(id)) {
      throw loi('VERSION_MISMATCH', 412, 'Phòng ban vừa được thay đổi ở nơi khác. Tải lại phòng ban rồi thực hiện lại thay đổi.');
    }
    kiemTen(du.name);
    if (du.parent_id !== null && du.parent_id !== p.parent_id) {
      if (nhanhCua(tatCa(), id).has(du.parent_id)) {
        throw loi('DEPARTMENT_CYCLE', 422, 'Không chuyển được phòng ban vào bên dưới chính nó hoặc một phòng ban con của nó. Chọn phòng ban cha khác.', [
          { pointer: '#/parent_id', detail: 'Không chọn được chính phòng ban này hoặc một phòng ban con của nó.' },
        ]);
      }
      kiemCha(du.parent_id, chieuCao(tatCa(), id));
    }
    const moi = { ...p, name: du.name, parent_id: du.parent_id };
    if (moi.name !== p.name || moi.parent_id !== p.parent_id) {
      cacPhong = cacPhong.map((x) => (x.id === id ? moi : x));
      tangPhienBan(id);
    }
    return { ...kemSo(moi), etag: etag(id) };
  },

  /** `DELETE /api/v1/departments/{id}` — chỉ khi không còn người và không còn phòng con. */
  xoa(id: string): void {
    const p = kemSo(tim(id));
    if (p.user_count > 0 || p.child_count > 0) {
      throw loi(
        'DEPARTMENT_NOT_EMPTY',
        409,
        `Phòng ban còn ${p.user_count} người dùng và ${p.child_count} phòng ban con nên chưa xoá được. Chuyển người dùng và phòng ban con sang nơi khác rồi xoá lại.`,
      );
    }
    cacPhong = cacPhong.filter((x) => x.id !== id);
  },
};
